import { isMobileDevice } from "./deviceDetection.util";
export { isMobileDevice };

/**
 * Camera Utility Functions
 * Handles virtual camera detection, device selection, and validation
 */

/**
 * Detect if a camera device is a virtual camera
 * @param {MediaDeviceInfo} device - Camera device to check
 * @returns {boolean} - True if virtual camera detected
 */
export const isVirtualCamera = (device) => {
  const label = device.label.toLowerCase();

  // Explicitly ALLOW "Windows Virtual Camera" as it is often a legitimate wrapper for real webcams (e.g. Phone Link, external webcam drivers)
  if (label.includes("windows virtual camera")) {
    return false;
  }

  const isMobile = isMobileDevice();

  // Common virtual camera software patterns
  const virtualCameraPatterns = [
    // OBS Studio Virtual Camera
    "obs virtual camera",
    "obs-camera",
    "obs cam",

    // ManyCam
    "manycam",
    "many cam",

    // XSplit VCam
    "xsplit",
    "vcam",

    // Snap Camera
    "snap camera",
    "snap cam",

    // CamTwist
    "camtwist",

    // ChromaCam
    "chromacam",

    // NVIDIA Broadcast
    "nvidia broadcast",
    "broadcast",

    // Logitech Capture
    "capture",

    // Generic virtual camera indicators
    "virtual",
    "virtual camera",
    "virtual cam",
    "fake",
    "fake camera",
    "dummy",
    "dummy camera",
    "simulator",
    "emulator",
    "software camera",
    "screen capture",
    "desktop capture",

    // NDI and streaming software
    "ndi",
    "newtek",
    "wirecast",
    "vmix",

    // Other streaming/virtual camera software
    "streamfx",
    "droidcam",
    "iriun",
    "epoccam",
    "camo",
    "reincubate camo",

    // Generic patterns that might indicate virtual cameras
    "mirror",
    "filter",
    "effect",

    // Phone cameras connected via Windows/Mac (only block on desktop if explicitly requested)
    // "windows virtual camera", // Termporarily removed as it blocks legitimate webcam usage in some browsers
    "mac virtual camera",
  ];

  // On mobile devices, allow mobile cameras (android camera, iphone camera, etc.)

  // On desktop, block mobile cameras used as external devices
  if (!isMobile) {
    // Desktop: Block mobile cameras used as external devices
    const mobileCameraPatterns = [
      "android camera",
      "iphone camera",
      "samsung camera",
      "phone camera",
    ];
    if (mobileCameraPatterns.some((pattern) => label.includes(pattern))) {
      return true;
    }
  }

  // Check if device label contains any virtual camera patterns
  return virtualCameraPatterns.some((pattern) => label.includes(pattern));
};

/**
 * Get preferred camera device (built-in over external/Bluetooth, excluding virtual cameras)
 * @returns {Promise<string|null>} - Device ID of preferred camera or null
 */
export const getPreferredCameraDevice = async () => {
  try {
    // Check if MediaDevices API is available
    if (!navigator.mediaDevices) {
      throw new Error(
        "Camera access is not supported in this browser. Please use a modern browser and ensure you're using HTTPS (required for camera access).",
      );
    }

    // Check if enumerateDevices is available
    if (typeof navigator.mediaDevices.enumerateDevices !== "function") {
      throw new Error(
        "Camera enumeration is not supported in this browser. Please use Chrome, Firefox, Safari, or Edge.",
      );
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(
      (device) => device.kind === "videoinput",
    );

    if (videoDevices.length === 0) {
      return null;
    }

    const isMobile = isMobileDevice();

    // Filter out virtual cameras first
    const realCameras = videoDevices.filter(
      (device) => !isVirtualCamera(device),
    );

    if (realCameras.length === 0) {
      // If only virtual cameras are available, throw an error
      throw new Error(
        "Only virtual cameras detected. Please use a physical camera for this assessment.",
      );
    }

    // On mobile devices, allow multiple cameras (front + back + additional back cameras)

    // STRICT: We MUST use the front camera only for assessment - no fallback to back cameras
    if (isMobile) {
      // Find front-facing camera on mobile - this is REQUIRED for assessment
      const frontCamera = realCameras.find((device) => {
        const label = device.label.toLowerCase();
        return (
          label.includes("front") ||
          label.includes("user") ||
          label.includes("facing") ||
          label.includes("selfie")
        );
      });

      if (frontCamera) {
        console.log(`📱 Mobile: Selected front camera: ${frontCamera.label}`);
        return frontCamera.deviceId;
      }

      // STRICT: If no front camera found, throw error - don't allow back camera fallback
      const availableCameras = realCameras.map((cam) => cam.label).join(", ");
      throw new Error(
        `Front camera not found on mobile device. Available cameras: ${availableCameras}. Please ensure your device has a front-facing camera enabled.`,
      );
    }

    // Desktop: Check for multiple real cameras (block external mobile cameras)
    if (realCameras.length > 1) {
      const cameraNames = realCameras.map((cam) => cam.label).join(", ");
      throw new Error(
        `Multiple camera devices detected: ${cameraNames}. Please disconnect external cameras and use only your built-in camera for the assessment.`,
      );
    }

    // Priority order for camera selection (among real cameras only):

    // 1. Built-in cameras (usually contain "integrated", "built-in", "facetime", or "internal")

    // 2. USB cameras (avoid Bluetooth and mobile)

    // 3. Any other real cameras (excluding Bluetooth and mobile)

    // First, try to find built-in cameras
    const builtInCamera = realCameras.find((device) => {
      const label = device.label.toLowerCase();
      return (
        label.includes("integrated") ||
        label.includes("built-in") ||
        label.includes("facetime") ||
        label.includes("internal") ||
        label.includes("laptop") ||
        (label.includes("webcam") && !label.includes("usb"))
      );
    });

    if (builtInCamera) {
      return builtInCamera.deviceId;
    }

    // Second, try to find USB cameras (avoid Bluetooth and mobile)
    const usbCamera = realCameras.find((device) => {
      const label = device.label.toLowerCase();
      return (
        !label.includes("bluetooth") &&
        !label.includes("phone") &&
        !label.includes("mobile") &&
        (label.includes("usb") || label.includes("webcam"))
      );
    });

    if (usbCamera) {
      return usbCamera.deviceId;
    }

    // Fallback to first real non-Bluetooth, non-mobile device
    const fallbackCamera = realCameras.find((device) => {
      const label = device.label.toLowerCase();
      return (
        !label.includes("bluetooth") &&
        !label.includes("phone") &&
        !label.includes("mobile")
      );
    });

    return fallbackCamera ? fallbackCamera.deviceId : realCameras[0].deviceId;
  } catch (error) {
    console.warn("Failed to enumerate camera devices:", error);
    throw error;
    // Re-throw to handle virtual camera errors properly
  }
};

/**
 * Get available real cameras (filters out virtual cameras)
 * @returns {Promise<Object>} - Camera info object
 */
export const getAvailableRealCameras = async () => {
  try {
    // Check if MediaDevices API is available
    if (!navigator.mediaDevices) {
      console.warn("MediaDevices API not available");
      return {
        allCameras: [],
        realCameras: [],
        virtualCameras: [],
        hasOnlyVirtualCameras: false,
        hasAnyVirtualCameras: false,
      };
    }

    // Check if enumerateDevices is available
    if (typeof navigator.mediaDevices.enumerateDevices !== "function") {
      console.warn("enumerateDevices not available");
      return {
        allCameras: [],
        realCameras: [],
        virtualCameras: [],
        hasOnlyVirtualCameras: false,
        hasAnyVirtualCameras: false,
      };
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(
      (device) => device.kind === "videoinput",
    );
    const realCameras = videoDevices.filter(
      (device) => !isVirtualCamera(device),
    );

    return {
      allCameras: videoDevices,
      realCameras: realCameras,
      virtualCameras: videoDevices.filter((device) => isVirtualCamera(device)),
      hasOnlyVirtualCameras:
        realCameras.length === 0 && videoDevices.length > 0,
      hasAnyVirtualCameras: videoDevices.some((device) =>
        isVirtualCamera(device),
      ),
    };
  } catch (error) {
    console.warn("Failed to enumerate camera devices:", error);
    return {
      allCameras: [],
      realCameras: [],
      virtualCameras: [],
      hasOnlyVirtualCameras: false,
      hasAnyVirtualCameras: false,
    };
  }
};

/**
 * Strict validation function that blocks if ANY virtual cameras are present or multiple cameras exist
 * @returns {Promise<Object>} - Camera info if valid
 */
export const validateCamerasStrict = async () => {
  const cameraInfo = await getAvailableRealCameras();
  const isMobile = isMobileDevice();

  if (cameraInfo.hasAnyVirtualCameras) {
    const virtualCameraNames = cameraInfo.virtualCameras
      .map((cam) => cam.label)
      .join(", ");
    throw new Error(
      `Virtual cameras detected: ${virtualCameraNames}. Please disconnect or disable all virtual camera software and refresh the page.`,
    );
  }

  if (cameraInfo.realCameras.length === 0) {
    throw new Error(
      "No physical cameras detected. Please connect a physical camera and refresh the page.",
    );
  }

  // On mobile: Allow any number of cameras (modern phones have multiple back cameras)

  // Just ensure we use the front camera - validation happens in getPreferredCameraDevice()

  /*
  // On desktop: Warn if > 1 camera (external camera detected), but don't block
  if (!isMobile && cameraInfo.realCameras.length > 1) {
    const cameraNames = cameraInfo.realCameras
      .map((cam) => cam.label)
      .join(", ");
    console.warn(
      `Multiple camera devices detected: ${cameraNames}. Ensure you use the correct camera.`
    );
  }
  */

  return cameraInfo;
};

/**
 * Default video constraints
 */
export const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: "user",
};

// Function to validate camera stream and ensure it's not from a virtual camera

// On mobile: Also validate that it's the front camera
export const validateCameraStream = async (stream) => {
  if (!stream || !stream.getVideoTracks) {
    throw new Error("Invalid camera stream");
  }

  const videoTracks = stream.getVideoTracks();
  if (videoTracks.length === 0) {
    throw new Error("No video tracks found in camera stream");
  }

  // Check the device label of the active track
  const activeTrack = videoTracks[0];
  const deviceLabel = activeTrack.label || "";
  const isMobile = isMobileDevice();

  if (isVirtualCamera({ label: deviceLabel })) {
    throw new Error(
      `Virtual camera detected in active stream: ${deviceLabel}. Please use a physical camera.`,
    );
  }

  // On mobile: Since we use facingMode constraint, the browser ensures correct camera selection

  // We don't need to validate labels - facingMode handles it

  // Just log for debugging purposes
  if (isMobile && deviceLabel) {
    console.log(`📱 Mobile camera in use: ${deviceLabel}`);
  }

  return true;
};
